import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth, formatApiError } from "../../lib/auth";
import { toast } from "sonner";
import { X, Lock } from "@phosphor-icons/react";

/**
 * ReservationDialog — visit intent for ACTIVE members only.
 * Non-logged-in users are prompted to sign in / become a member.
 * Logged-in but non-active members see a status-required message.
 */
export default function ReservationDialog({ merchant, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState(2);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState(false);
  const [memberStatus, setMemberStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "member") { setChecking(false); return; }
    api.get("/membership/my-status")
      .then(({ data }) => setMemberStatus(data.status))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) { toast.error("Please accept the Terms & Conditions"); return; }
    setLoading(true);
    try {
      await api.post("/reservations", {
        merchant_id: merchant.id,
        visit_date: date || null,
        guest_count: Number(guests),
        notes,
        accepted_terms: true,
      });
      toast.success("Visit registered. Present your member QR at the venue.");
      onClose();
      nav("/me");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  // 1) Not logged in
  if (!user) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" data-testid="reservation-dialog">
        <div className="relative max-w-md w-full bg-[#0a0a0a] border border-[#D4AF37]/40 rounded-md p-7 text-center">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white p-1.5" data-testid="reservation-dialog-close"><X size={20} /></button>
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#0d0d0d] border border-[#D4AF37]/40 text-[#D4AF37]"><Lock size={22} weight="duotone" /></div>
          <p className="font-display text-2xl text-white mt-4">Members Only</p>
          <p className="text-white/60 text-sm mt-3">The Social Circle is a private membership network. Sign in to register your visit, or activate your membership to begin.</p>
          <div className="mt-6 flex flex-col gap-3">
            <button onClick={() => { onClose(); nav("/login"); }} className="tsc-btn-outline" data-testid="reservation-cta-login">Sign in</button>
            <button onClick={() => { onClose(); nav("/register"); }} className="tsc-btn-gold" data-testid="reservation-cta-register">Become a Member</button>
          </div>
        </div>
      </div>
    );
  }

  // 2) Logged in but not active
  if (user.role !== "member" || (memberStatus && memberStatus !== "active")) {
    const statusLabel = {
      pending_payment: "Payment required",
      pending_verification: "Pending verification",
      rejected: "Membership not approved",
      expired: "Membership expired",
      suspended: "Membership suspended",
    }[memberStatus] || (user.role !== "member" ? "Not a member account" : "Membership not active");

    return (
      <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" data-testid="reservation-dialog">
        <div className="relative max-w-md w-full bg-[#0a0a0a] border border-[#D4AF37]/40 rounded-md p-7 text-center">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white p-1.5"><X size={20} /></button>
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#0d0d0d] border border-[#D4AF37]/40 text-[#D4AF37]"><Lock size={22} weight="duotone" /></div>
          <p className="font-display text-2xl text-white mt-4">{statusLabel}</p>
          <p className="text-white/65 text-sm mt-3">Your membership must be approved before you can register visits and access partner privileges.</p>
          {user.role === "member" && (
            <button onClick={() => { onClose(); nav("/membership/payment"); }} className="tsc-btn-gold w-full mt-6" data-testid="reservation-cta-payment">View payment status</button>
          )}
        </div>
      </div>
    );
  }

  // 3) Active member — show reservation form
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" data-testid="reservation-dialog">
      <div className="relative max-w-md w-full bg-[#0a0a0a] border border-[#D4AF37]/40 rounded-md">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white p-1.5"><X size={20} /></button>
        <div className="p-7">
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Register your visit</p>
          <p className="font-display text-2xl text-white mt-2 leading-tight">{merchant.business_name}</p>
          <p className="text-white/60 text-sm mt-1">Present your member QR at the venue — your discount will be applied automatically.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Date / time</label>
                <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} className="tsc-input mt-1.5" data-testid="reservation-input-date" />
              </div>
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Guests</label>
                <input type="number" min="1" max="50" value={guests} onChange={(e) => setGuests(e.target.value)} className="tsc-input mt-1.5" data-testid="reservation-input-guests" />
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-luxe uppercase text-white/60">Special requests (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2" className="tsc-input mt-1.5" placeholder="e.g. window table, birthday celebration…" data-testid="reservation-input-notes" />
            </div>

            <div className="border border-white/10 bg-[#070707] rounded-md p-4 max-h-44 overflow-y-auto" data-testid="reservation-merchant-terms">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{merchant.business_name} · Terms & Conditions</p>
              <p className="text-white/65 text-xs mt-2 whitespace-pre-wrap leading-relaxed">
                {merchant.terms_and_conditions?.trim()
                  ? merchant.terms_and_conditions
                  : "Member privileges are subject to merchant validation at point of sale. Cannot be combined with other promotions unless stated. Terms may apply to specific items, blackout dates, or minimum spend. Please confirm with the venue staff before ordering."}
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-2">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 accent-[#D4AF37]" data-testid="reservation-input-terms" />
              <span className="text-white/65 text-xs leading-relaxed">I agree to this merchant's Terms and Conditions and understand that discounts are subject to merchant validation.</span>
            </label>
            <button type="submit" disabled={loading} className="tsc-btn-gold w-full mt-2" data-testid="reservation-submit">
              {loading ? "Registering…" : "Confirm Visit Intent"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
